import React, { useState } from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    CircularProgress,
    TextField,

} from '@mui/material';
import SearchBox from '../SearchBox';
import { executeFetchAsync } from '../../Util';
import { config } from '../../Const';
import { DisDatType } from '../Const';
import { v4 as uuidv4 } from 'uuid';

const disDatTypes = [
    { label: 'Artist vs. Artist', value: DisDatType.CROSS_ENTITY_MUSICIAN_VS_MUSICIAN },
    { label: 'Group vs. Group', value: DisDatType.CROSS_ENTITY_MUSICAL_GROUP_VS_MUSICAL_GROUP },
    { label: 'Musical Group Album vs. Album', value: DisDatType.MUSICAL_GROUP_ALBUM_VS_ALBUM },
    { label: 'Musician Album vs. Album', value: DisDatType.MUSICIAN_ALBUM_VS_ALBUM },
    { label: 'Group Member vs. Group Member', value: DisDatType.MUSICAL_GROUP_MEMBER_VS_MEMBER },
    { label: 'Custom', value: DisDatType.CUSTOM },
];

const endPointMap = {
    [DisDatType.CROSS_ENTITY_MUSICIAN_VS_MUSICIAN]: 'musician',
    [DisDatType.CROSS_ENTITY_MUSICAL_GROUP_VS_MUSICAL_GROUP]: 'musicalgroup',
    [DisDatType.MUSICAL_GROUP_ALBUM_VS_ALBUM]: 'musicalgroup',
    [DisDatType.MUSICIAN_ALBUM_VS_ALBUM]: 'musician',
    [DisDatType.MUSICAL_GROUP_MEMBER_VS_MEMBER]: 'musicalgroup'
};


export default function OptionSelector({
    type,
    selectedArtistOrGroup,
    entity1,
    entity2,
    customInstructions,
    onSelect
}) {


    const [options, setOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);


    const createQuizzlerObject = (entity, idKey) => ({
        idMapping: { [idKey]: entity.id }
    });

    const createQVariable = (entity, idKey) => ({
        label: entity.label,
        quizzlerObject: createQuizzlerObject(entity, idKey)
    });

    const getIdKeyByDisDatType = (disDatType) => {
        switch (disDatType) {
            case DisDatType.MUSICAL_GROUP_ALBUM_VS_ALBUM:
            case DisDatType.MUSICIAN_ALBUM_VS_ALBUM:
                return 'MUSICBRAINZ';
            case DisDatType.MUSICAL_GROUP_MEMBER_VS_MEMBER:
            case DisDatType.CROSS_ENTITY_MUSICIAN_VS_MUSICIAN:
            case DisDatType.CROSS_ENTITY_MUSICAL_GROUP_VS_MUSICAL_GROUP:
            default:
                return 'WIKIDATA';
        }
    };

    const isQVariable = (obj) => obj && obj.quizzlerObject;

    const wrapIfRawEntity = (entity, idKey) => {
        if (!entity) return null;
        return isQVariable(entity) ? entity : createQVariable(entity, idKey);
    };

    const updateSelection = (newState) => {
        const disdatType = newState.type ?? type;
        const idKey = getIdKeyByDisDatType(disdatType);

        const updatedSelection = {
            type: disdatType,
            selectedArtistOrGroup: newState.selectedArtistOrGroup ?? selectedArtistOrGroup,
            entity1: wrapIfRawEntity(newState.entity1 ?? entity1, idKey),
            entity2: wrapIfRawEntity(newState.entity2 ?? entity2, idKey),
            customInstructions: newState.customInstructions ?? customInstructions
        }

        onSelect(updatedSelection);
    };



    const getEndPointByType = () => endPointMap[type] || 'entity';

    const handleTypeChange = (e) => {
        updateSelection({
            type: e.target.value,
            selectedArtistOrGroup: null,
            entity1: null,
            entity2: null
        });
        setOptions([]);
    };

    const handleEntity1 = (e) => {
        const selectedId = e.target.value;
        const idKey = getIdKeyByDisDatType(type);

        const selected = options.find(
            (opt) => opt?.quizzlerObject?.idMapping?.[idKey] === selectedId
        );

        if (selected) {
            updateSelection({ entity1: selected });
        }
    };

    const handleEntity2 = (e) => {
        const selectedId = e.target.value;
        const idKey = getIdKeyByDisDatType(type);

        const selected = options.find(
            (opt) => opt?.quizzlerObject?.idMapping?.[idKey] === selectedId
        );

        if (selected) {
            updateSelection({ entity2: selected });
        }
    };

    const handleArtistOrGroup = (entity) => {

        const wrapped = createQuizzlerObject(entity, 'WIKIDATA');

        updateSelection({
            selectedArtistOrGroup: wrapped,
            entity1: null,
            entity2: null
        });


        fetchSubEntities(wrapped);

    };

    const fetchSubEntities = async (parentQuizzlerObject) => {
        const wikidataId = parentQuizzlerObject?.idMapping?.WIKIDATA;
        if (!wikidataId) return;

        setIsLoading(true);

        try {
            const disDatType = getDisDatTypeForSubEntityFetch(type);

            const payload = {
                'type': 'DisDatQuestionParams',
                disDatType,
                quizzlerEntity: {
                    idMapping: {
                        WIKIDATA: wikidataId
                    }
                }
            };

            const response = await executeFetchAsync(
                `${config.SERVER_URL}/api/v1/admin/disdat/generate`,
                'post',
                { 'Content-Type': 'application/json' },
                JSON.stringify(payload)
            );

            setOptions(response?.criteria?.disDatOptionQVariableList || []);
        } catch (error) {
            console.error("Failed to fetch sub-entities via generate:", error);
            setOptions([]);
        } finally {
            setIsLoading(false);
        }
    };


    const getDisDatTypeForSubEntityFetch = (type) => {
        switch (type) {
            case DisDatType.MUSICAL_GROUP_MEMBER_VS_MEMBER:
            case DisDatType.MUSICAL_GROUP_ALBUM_VS_ALBUM:
            case DisDatType.MUSICIAN_ALBUM_VS_ALBUM:
                return type;
            default:
                return null;
        }
    };



    const renderSelect = (label, qVariable, onChange) => {
        const idKey = getIdKeyByDisDatType(type);
        const currentId = qVariable?.quizzlerObject?.idMapping?.[idKey] ?? '';

        return (
            <FormControl fullWidth size="small">
                <InputLabel>{label}</InputLabel>
                <Select value={currentId} label={label} onChange={onChange}>
                    {options.map((opt) => {
                        const optId = opt?.quizzlerObject?.idMapping?.[idKey];
                        return (
                            <MenuItem key={optId} value={optId}>
                                {opt.label}
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>
        );
    };

    const generateSearchBoxValue = (quizzlerObject) => {

        //TODO - need to fetch the label from the server for custom entries since they won't have a label at this point. We could also consider enforcing that the SearchBox value is always in the QVariable format to avoid this issue entirely
        if (!quizzlerObject) return null;
        const idKey = getIdKeyByDisDatType(type);

        const retval = { id: quizzlerObject?.idMapping?.[idKey] ?? null, label: quizzlerObject.label, type: type };

        console.log("quizzlerObject:", quizzlerObject);
        console.log("Generated value passed to SearchBox:", retval);

        return retval;

    };



    return (
        <Box>
            <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                <InputLabel>DisDat Type</InputLabel>
                <Select value={type} label="DisDat Type" onChange={handleTypeChange}>
                    {disDatTypes.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {type === DisDatType.CUSTOM && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="First Entity"
                            value={entity1?.label || ''}
                            onChange={(e) => {
                                const value = e.target.value;
                                updateSelection({
                                    entity1: {
                                        label: value,
                                        quizzlerObject: {
                                            label: value,
                                            idMapping: {
                                                QUIZMYDAY: uuidv4()
                                            }
                                        }
                                    }
                                });
                            }}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label="Second Entity"
                            value={entity2?.label || ''}
                            onChange={(e) => {
                                const value = e.target.value;
                                updateSelection({
                                    entity2: {
                                        label: value,
                                        quizzlerObject: {
                                            label: value,
                                            idMapping: {
                                                QUIZMYDAY: uuidv4()
                                            }
                                        }
                                    }
                                });
                            }}
                        />
                    </Box>
                    <TextField
                        fullWidth
                        size="small"
                        label="Custom AI Instructions"
                        value={customInstructions || ''}
                        onChange={(e) => updateSelection({ customInstructions: e.target.value })}
                    />
                </Box>
            )}


            {(type === DisDatType.CROSS_ENTITY_MUSICIAN_VS_MUSICIAN || type === DisDatType.CROSS_ENTITY_MUSICAL_GROUP_VS_MUSICAL_GROUP) && (
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <SearchBox
                        key={'type1' + type}
                        value={entity1}
                        endPoint={getEndPointByType()}
                        onEntitySelected={(e) => updateSelection({ entity1: e })}
                        resultMappingFunction={(r) => ({ id: r.idMapping.WIKIDATA, label: r.label, type: r.entityType })}
                        small
                        defaultText={`Choose ${type === DisDatType.CROSS_ENTITY_MUSICIAN_VS_MUSICIAN ? 'Artist' : 'Group'} 1`}
                    />
                    <SearchBox
                        value={entity2}
                        key={'type2' + type}
                        endPoint={getEndPointByType()}
                        onEntitySelected={(e) => updateSelection({ entity2: e })}
                        resultMappingFunction={(r) => ({ id: r.idMapping.WIKIDATA, label: r.label, type: r.entityType })}
                        small
                        defaultText={`Choose ${type === DisDatType.CROSS_ENTITY_MUSICIAN_VS_MUSICIAN ? 'Artist' : 'Group'} 2`}
                    />
                </Box>
            )}

            {(type === DisDatType.MUSICAL_GROUP_ALBUM_VS_ALBUM || type === DisDatType.MUSICAL_GROUP_MEMBER_VS_MEMBER || type === DisDatType.MUSICIAN_ALBUM_VS_ALBUM) && (
                <>
                    <SearchBox
                        endPoint={getEndPointByType()}
                        onEntitySelected={handleArtistOrGroup}
                        value={generateSearchBoxValue(selectedArtistOrGroup)}
                        resultMappingFunction={(r) => ({ id: r.idMapping.WIKIDATA, label: r.label, type: r.entityType })}
                        small
                        defaultText={type === DisDatType.MUSICAL_GROUP_ALBUM_VS_ALBUM ? 'Choose Musical Group' : type === DisDatType.MUSICIAN_ALBUM_VS_ALBUM ? 'Choose Musician' : 'Choose Group'}
                    />
                    {selectedArtistOrGroup && (
                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                            {isLoading ? (
                                <CircularProgress size={24} />
                            ) : (
                                <>
                                    {renderSelect(
                                        `Choose First ${type === DisDatType.MUSICAL_GROUP_ALBUM_VS_ALBUM || type === DisDatType.MUSICIAN_ALBUM_VS_ALBUM ? 'Album' : 'Member'}`,
                                        entity1,
                                        handleEntity1
                                    )}
                                    {renderSelect(
                                        `Choose Second ${type === DisDatType.MUSICAL_GROUP_ALBUM_VS_ALBUM || type === DisDatType.MUSICIAN_ALBUM_VS_ALBUM ? 'Album' : 'Member'}`,
                                        entity2,
                                        handleEntity2
                                    )}
                                </>
                            )}


                        </Box>
                    )}
                </>
            )}


        </Box>
    );
}
